import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Typography,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import "../../../common/commonStyles.css";
import { hasSearchableProductQuery, filterAndSortProductsForSearch } from "../../../../productSearch/matchProductTokens";
import productSplitService from "../../../../services/ProductSplitService";

const resolveAvailableOptions = (product) => {
    const splitValue = String(product?.split ?? "2").trim();
    if (splitValue === "0") return ["Box"];
    if (splitValue === "1") return ["Piece"];
    return ["Box", "Piece"];
};

const resolveDefaultOrderType = (product, customerId = null) => {
    const splitValue = String(product?.split ?? "2").trim();
    if (splitValue === "0") return "Box";
    if (splitValue === "1") return "Piece";
    if (customerId && product?.id) {
        const stored = productSplitService.getDefaultOrderType(customerId, product.id);
        if (stored === "Box" || stored === "Piece") return stored;
    }
    return "Box";
};

const InventorySearchModal = ({
    open,
    onClose,
    onProductSelect,
    productOptions = [],
    isLoading = false,
    productSearchInput = "",
    productSearchMode = "code",
    selectedCustomer,
    anchorPosition = null,
    onFocusOuterProductSearch,
}) => {
    const [selectedType, setSelectedType] = useState("Box");
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [availableOptions, setAvailableOptions] = useState(["Box", "Piece"]);
    const [rowTypes, setRowTypes] = useState({});

    const customerId = selectedCustomer?.id ?? null;

    const syncTypeFromProduct = (product) => {
        if (!product) {
            setAvailableOptions(["Box", "Piece"]);
            setSelectedType("Box");
            return;
        }
        const options = resolveAvailableOptions(product);
        const defaultType = resolveDefaultOrderType(product, customerId);
        const productKey = product.id;
        const rowType = rowTypes[productKey] || defaultType;
        setAvailableOptions(options);
        setSelectedType(options.includes(rowType) ? rowType : defaultType);
    };

    const selectRef = useRef(null);
    const tableContainerRef = useRef(null);

    const filteredProducts = useMemo(
        () => filterAndSortProductsForSearch(productOptions, productSearchInput, productSearchMode),
        [productOptions, productSearchInput, productSearchMode]
    );

    useEffect(() => {
        if (open) {
            setSelectedRowIndex(null);
            setSelectedType("Box");
            setAvailableOptions(["Box", "Piece"]);
            setRowTypes({});
        }
    }, [open]);

    useEffect(() => {
        if (!open || filteredProducts.length === 0) {
            return;
        }

        setRowTypes((prev) => {
            const updated = { ...prev };
            let hasChanges = false;
            filteredProducts.forEach((product) => {
                const productKey = product.id;
                if (!Object.prototype.hasOwnProperty.call(updated, productKey)) {
                    updated[productKey] = resolveDefaultOrderType(product, customerId);
                    hasChanges = true;
                }
            });
            return hasChanges ? updated : prev;
        });

        const targetIndex =
            selectedRowIndex !== null && selectedRowIndex < filteredProducts.length
                ? selectedRowIndex
                : 0;
        const product = filteredProducts[targetIndex];
        const options = resolveAvailableOptions(product);
        const defaultType = resolveDefaultOrderType(product, customerId);
        setAvailableOptions(options);
        setSelectedType(defaultType);
        if (selectedRowIndex === null || selectedRowIndex >= filteredProducts.length) {
            setSelectedRowIndex(0);
        }
    }, [filteredProducts, open, customerId]);

    const handleTypeChange = (event) => {
        const newType = event.target.value;
        setSelectedType(newType);

        if (selectedRowIndex !== null && filteredProducts[selectedRowIndex]) {
            const product = filteredProducts[selectedRowIndex];
            const productKey = product.id;
            setRowTypes(prev => ({
                ...prev,
                [productKey]: newType
            }));
        }
    };

    const updateOptionsBasedOnSplit = (product) => {
        syncTypeFromProduct(product);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (filteredProducts.length > 0) {
                event.preventDefault();
                setSelectedRowIndex(0);
                updateOptionsBasedOnSplit(filteredProducts[0]);
                setTimeout(() => {
                    const firstRow = tableContainerRef.current?.querySelector('tbody tr');
                    if (firstRow) {
                        firstRow.focus();
                    }
                }, 0);
            }
        }
    };

    const scrollRowIntoView = (rowIndex) => {
        const rowEl = tableContainerRef.current?.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
        if (rowEl && rowEl.scrollIntoView) {
            rowEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        return rowEl;
    };

    const moveSelectionByStep = (step, focusRow = true) => {
        if (filteredProducts.length === 0) {
            return;
        }
        const baseIndex = selectedRowIndex === null ? (step > 0 ? -1 : 0) : selectedRowIndex;
        const nextIndex = Math.max(0, Math.min(baseIndex + step, filteredProducts.length - 1));
        setSelectedRowIndex(nextIndex);
        updateOptionsBasedOnSplit(filteredProducts[nextIndex]);
        const rowEl = scrollRowIntoView(nextIndex);
        if (focusRow) {
            if (rowEl) {
                rowEl.focus();
            }
        }
    };

    const toggleRowType = (direction = 'next') => {
        if (selectedRowIndex === null || availableOptions.length <= 1) {
            return;
        }
        const product = filteredProducts[selectedRowIndex];
        if (!product) {
            return;
        }
        const productKey = product.id;
        const currentRowType = rowTypes[productKey] || selectedType;
        const currentIndex = availableOptions.indexOf(currentRowType);
        let newType = availableOptions[0];
        if (direction === 'prev') {
            const prevIndex = (currentIndex - 1 + availableOptions.length) % availableOptions.length;
            newType = availableOptions[prevIndex];
        } else {
            const nextIndex = (currentIndex + 1) % availableOptions.length;
            newType = availableOptions[nextIndex];
        }
        setSelectedType(newType);
        setRowTypes(prev => ({
            ...prev,
            [productKey]: newType
        }));
    };

    const handleTableKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveSelectionByStep(1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveSelectionByStep(-1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            toggleRowType(event.key === 'ArrowLeft' ? 'prev' : 'next');
        } else if (event.key === 'Tab') {
            event.preventDefault();
            toggleRowType('next');
        } else if (event.key === '/') {
            event.preventDefault();
            if (typeof onFocusOuterProductSearch === "function") {
                onFocusOuterProductSearch();
            }
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const currentIndex = selectedRowIndex === null ? 0 : selectedRowIndex;
            const product = filteredProducts[currentIndex];
            if (product) {
                handleProductSelect(product);
            }
        }
    };

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const handleGlobalPopupNavigation = (event) => {
            const key = event.key;
            const target = event.target;
            const isTypingTarget =
                target?.tagName === 'INPUT' ||
                target?.tagName === 'TEXTAREA' ||
                target?.isContentEditable;
            if (isTypingTarget) {
                if (key === 'ArrowDown') {
                    event.preventDefault();
                    moveSelectionByStep(1, false);
                } else if (key === 'ArrowUp') {
                    event.preventDefault();
                    moveSelectionByStep(-1, false);
                } else if (key === 'Enter') {
                    const index = selectedRowIndex === null ? 0 : selectedRowIndex;
                    const product = filteredProducts[index];
                    if (product) {
                        event.preventDefault();
                        handleProductSelect(product);
                    }
                }
                return;
            }
            if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab'].includes(key)) {
                return;
            }
            if (filteredProducts.length === 0) {
                return;
            }
            if (key === 'ArrowDown') {
                event.preventDefault();
                moveSelectionByStep(1, false);
                return;
            }
            if (key === 'ArrowUp') {
                event.preventDefault();
                moveSelectionByStep(-1, false);
                return;
            }
            if (key === 'ArrowLeft') {
                event.preventDefault();
                toggleRowType('prev');
                return;
            }
            if (key === 'ArrowRight' || key === 'Tab') {
                event.preventDefault();
                toggleRowType('next');
                return;
            }
            if (key === 'Enter') {
                const index = selectedRowIndex === null ? 0 : selectedRowIndex;
                const product = filteredProducts[index];
                if (product) {
                    event.preventDefault();
                    handleProductSelect(product);
                }
            }
        };
        window.addEventListener('keydown', handleGlobalPopupNavigation);
        return () => {
            window.removeEventListener('keydown', handleGlobalPopupNavigation);
        };
    }, [open, filteredProducts, selectedRowIndex, availableOptions, rowTypes, selectedType]);

    const handleProductSelect = (product) => {
        if (!product) return;

        const productKey = product.id;
        const rowType = rowTypes[productKey] || resolveDefaultOrderType(product, customerId);
        const typeToUse = rowType || resolveDefaultOrderType(product, customerId);

        const productWithType = {
            ...product,
            order_type: typeToUse,
            split: typeToUse === "Piece" ? "1" : "0",
            price: typeToUse === "Box"
                ? (product.final_price || product.price || "0")
                : (product.final_split_price || product.price || "0")
        };

        onProductSelect(productWithType);
    };

    const handleRowClick = (product, index) => {
        setSelectedRowIndex(index);
        const productKey = product.id;
        const rowType = rowTypes[productKey];
        if (rowType) {
            setSelectedType(rowType);
        }
        updateOptionsBasedOnSplit(product);
        handleProductSelect(product);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            hideBackdrop
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            BackdropProps={{
                sx: {
                    backgroundColor: 'transparent'
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxHeight: '90vh',
                    mt: 0,
                    overflow: 'visible',
                    ...(anchorPosition ? {
                        position: 'fixed',
                        top: anchorPosition.top,
                        m: 0
                    } : {})
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 700,
                fontSize: '1.25rem',
                px: 3,
                py: 1.5,
                borderBottom: '1px solid #E0E0E0'
            }}>
                Add Product
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> 
                <FormControl size="small">
                    <Select
                        className="input-border"
                        ref={selectRef}
                        value={selectedType}
                        onChange={handleTypeChange}
                        onKeyDown={handleKeyDown}
                        disabled={availableOptions.length === 1}
                        sx={{
                            height: '40px',
                            width: '350px',
                            backgroundColor: '#fff',
                            '& .MuiSelect-select': {
                                fontWeight: 600,
                                fontSize: '0.875rem',
                            }
                        }}
                    >
                        {availableOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <IconButton
                    onClick={onClose}
                    sx={{ color: 'text.secondary' }}
                    size="small"
                >
                    <Iconify icon="mdi:close" width={24} height={24} />
                </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3, mt: 2, overflow: 'visible' }}>
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    className="product-picker-table-container"
                    sx={{
                        height: 'calc(80vh - 140px)',
                        minHeight: '300px',
                        maxHeight: 'calc(80vh - 140px)',
                        border: '1px solid #E0E0E0',
                        overflow: 'auto',
                        '& .MuiTableRow-root:focus': {
                            outline: '2px solid #1976d2',
                            outlineOffset: '-2px'
                        }
                    }}
                >
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead className="table-cell-header">
                            <TableRow>
                                <TableCell title="Code" sx={{ fontSize: '0.813rem!important', width: '110px', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Code</TableCell>
                                <TableCell title="Product Name" sx={{ fontSize: '0.813rem!important', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Product Name</TableCell>
                                <TableCell title="Type" sx={{ fontSize: '0.813rem!important', width: '70px', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Type</TableCell>
                                <TableCell title="Selling Price" align="right" sx={{ fontSize: '0.813rem!important', width: '120px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Selling Price</TableCell>
                                <TableCell title="Offer Price" align="right" sx={{ fontSize: '0.813rem!important', width: '120px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Offer Price</TableCell>
                                <TableCell title="Discount" align="right" sx={{ fontSize: '0.813rem!important', width: '100px', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Discount</TableCell>
                                <TableCell title="Stock" align="right" sx={{ fontSize: '0.813rem!important', width: '80px', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Stock</TableCell>
                                <TableCell title="VAT (%)" align="right" sx={{ fontSize: '0.813rem!important', width: '90px', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>VAT (%)</TableCell>
                                <TableCell title="Pack Size" align="right" sx={{ fontSize: '0.813rem!important', width: '100px', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Pack Size</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredProducts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        {!selectedCustomer?.id ? (
                                            <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                                Please select a customer first
                                            </Typography>
                                        ) : !hasSearchableProductQuery(productSearchInput) ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <Iconify
                                                    icon="solar:cart-4-broken"
                                                    width={48}
                                                    height={48}
                                                    sx={{ color: 'text.disabled' }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    color="text.secondary"
                                                    sx={{ fontSize: '0.875rem' }}
                                                >
                                                    No products yet !
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: '0.75rem' }}
                                                >
                                                    Type at least 2 characters in the product search on the order screen
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                No products found
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredProducts.map((product, index) => {
                                    const splitValue = String(product.split || "2");
                                    let orderTypeDisplay = "";
                                    if (splitValue === "0") {
                                        orderTypeDisplay = "Box";
                                    } else if (splitValue === "1") {
                                        orderTypeDisplay = "Piece";
                                    } else {
                                        orderTypeDisplay = "Box/Piece";
                                    }

                                    const productKey = product.id;
                                    const rowType =
                                        rowTypes[productKey] || resolveDefaultOrderType(product, customerId);

                                    const sellingPrice = rowType === "Box"
                                        ? parseFloat(product.price || 0)
                                        : parseFloat(product.split_price || 0);

                                    const offerPrice = rowType === "Box"
                                        ? parseFloat(product.final_price || 0)
                                        : parseFloat(product.final_split_price || 0);
// 
                                    // const discount = sellingPrice - offerPrice;
                                    const discount = offerPrice > 0 ? sellingPrice - offerPrice : 0;

                                    const packSize = product.noOfPieceInBox || product.split || 'N/A';
                                    const boxQtyParsed = parseFloat(
                                        product.availableBoxQty ?? product.available_box_qty ?? NaN
                                    );
                                    const pieceQtyParsed = parseFloat(
                                        product.availablePieceQty ?? product.available_piece_qty ?? NaN
                                    );
                                    const legacyStock = parseFloat(product.stock ?? 0) || 0;
                                    const stockForRow =
                                        splitValue === "0"
                                            ? (Number.isFinite(boxQtyParsed) ? boxQtyParsed : legacyStock)
                                            : splitValue === "1"
                                                ? (Number.isFinite(pieceQtyParsed) ? pieceQtyParsed : legacyStock)
                                                : rowType === "Piece"
                                                    ? (Number.isFinite(pieceQtyParsed) ? pieceQtyParsed : legacyStock)
                                                    : (Number.isFinite(boxQtyParsed) ? boxQtyParsed : legacyStock);

                                    return (
                                        <TableRow
                                            className={`product-picker-table-row${selectedRowIndex === index ? " product-picker-table-row--active" : ""}`}
                                            key={product.id}
                                            onClick={() => handleRowClick(product, index)}
                                            onKeyDown={handleTableKeyDown}
                                            tabIndex={0}
                                            sx={{
                                                "&:focus": {
                                                    outline: "none",
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ fontSize: '0.813rem' }}>{product.product_code || 'N/A'}</TableCell>
                                            <TableCell
                                                title={product.product_name || 'N/A'}
                                                sx={{
                                                    fontSize: '0.813rem',
                                                    maxWidth: 0,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {product.product_name || 'N/A'}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.813rem', fontWeight: 500 }}>{orderTypeDisplay}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.813rem' }}>£{sellingPrice.toFixed(2)}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.813rem' }}>£{offerPrice.toFixed(2)}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.813rem' }}>£{discount.toFixed(2)}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.813rem' }}>{stockForRow}</TableCell>

                                            <TableCell align="right" sx={{ fontSize: '0.813rem' }}>{parseFloat(product.rate || product.tax_rate || 0).toFixed(0)}%</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.813rem' }}>{packSize}</TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* <Divider sx={{ my: 2 }} /> */}

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    mt: 1,
                    // p: 2, 
                    // backgroundColor: '#F8F9FA', 
                    // borderRadius: '8px',
                    // border: '1px solid #E0E0E0'
                }}>
                    {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Iconify icon="solar:keyboard-bold" width={20} height={20} sx={{ color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                            Keyboard Shortcuts
                        </Typography>
                    </Box> */}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Box component="kbd" sx={{
                                    px: 1,
                                    py: 0.5,
                                    fontSize: '0.7rem',
                                    color: '#212121',
                                    fontWeight: 600,
                                    backgroundColor: '#fff',
                                    border: '1px solid rgb(13, 13, 14)',
                                    borderRadius: '4px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    fontFamily: 'monospace'
                                }}>
                                    ↑
                                </Box>
                                <Box component="kbd" sx={{
                                    px: 1,
                                    py: 0.5,
                                    fontSize: '0.7rem',
                                    color: '#212121',
                                    fontWeight: 600,
                                    backgroundColor: '#fff',
                                    border: '1px solid rgb(13, 13, 14)',
                                    borderRadius: '4px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    fontFamily: 'monospace'
                                }}>
                                    ↓
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                Navigate rows
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="kbd" sx={{
                                px: 1,
                                py: 0.5,
                                fontSize: '0.7rem',
                                color: '#212121',
                                fontWeight: 600,
                                backgroundColor: '#fff',
                                border: '1px solid rgb(13, 13, 14)',
                                borderRadius: '4px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                fontFamily: 'monospace'
                            }}>
                                Tab
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                Toggle Box/Piece
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="kbd" sx={{
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.7rem',
                                color: '#212121',
                                fontWeight: 600,
                                backgroundColor: '#fff',
                                border: '1px solid rgb(13, 13, 14)',
                                borderRadius: '4px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                fontFamily: 'monospace'
                            }}>
                                Enter
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                Add product
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="kbd" sx={{
                                px: 1,
                                py: 0.5,
                                fontSize: '0.7rem',
                                color: '#212121',
                                fontWeight: 600,
                                backgroundColor: '#fff',
                                border: '1px solid rgb(13, 13, 14)',
                                borderRadius: '4px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                fontFamily: 'monospace'
                            }}>
                                /
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                Focus order search
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="kbd" sx={{
                                px: 1,
                                py: 0.5,
                                fontSize: '0.7rem',
                                color: '#212121',
                                fontWeight: 600,
                                backgroundColor: '#fff',
                                border: '1px solid rgb(13, 13, 14)',
                                borderRadius: '4px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                fontFamily: 'monospace'
                            }}>
                                Esc
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                Close dialog
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default InventorySearchModal;

